'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface PricingCardsProps {
  lang?: 'it' | 'en';
}

const packages = [
  {
    id: 'pkg_first_exp',
    nameIT: 'Prima Esperienza',
    nameEN: 'First Experience',
    taglineIT: 'Il modo migliore per iniziare',
    taglineEN: 'The best way to start',
    durationIT: '1 ora',
    durationEN: '1 hour',
    price: 120,
    depositAllowed: false,
    featuresIT: [
      'Briefing sicurezza 15 min',
      'Casco certificato CE',
      'Giubbotto salvagente',
      'Impact vest',
      'Istruttore in acqua',
      'Assicurazione inclusa',
      'Muta (se necessaria)',
    ],
    featuresEN: [
      '15-min safety briefing',
      'CE-certified helmet',
      'Life jacket',
      'Impact vest',
      'In-water instructor',
      'Insurance included',
      'Wetsuit (if needed)',
    ],
    notIncludedIT: ['Video Insta360 (add-on)', 'Deposito 30% non disponibile'],
    notIncludedEN: ['Insta360 video (add-on)', '30% deposit not available'],
    color: '#00D4FF',
    descIT: 'Perfetta per chi vuole scoprire l\'e-foil per la prima volta. Il nostro istruttore certificato ti guida passo passo, dal briefing a terra fino ai primi voli sull\'acqua.',
    descEN: 'Perfect for discovering e-foil for the first time. Our certified instructor guides you step by step, from the land briefing to your first flights above the water.',
  },
  {
    id: 'pkg_4h',
    nameIT: '4 Ore',
    nameEN: '4 Hours',
    taglineIT: 'La scelta dei nostri ospiti',
    taglineEN: 'Our guests\' choice',
    durationIT: '4 ore',
    durationEN: '4 hours',
    price: 380,
    depositAllowed: true,
    featuresIT: [
      'Tutto incluso: attrezzatura, briefing e istruttore',
      'Pensato per 2 persone, con gestione flessibile',
      'Scegli tra esperienza in coppia nello stesso giorno o due sessioni in giorni diversi',
      'Date selezionabili direttamente in prenotazione',
      'Possibilità di aggiungere il video Insta360 dell\'esperienza',
    ],
    featuresEN: [
      'Everything included: equipment, briefing and instructor',
      'Designed for 2 people, with flexible management',
      'Choose between a couples session on the same day or two sessions on different days',
      'Dates selectable directly during booking',
      'Option to add the Insta360 video of your experience',
    ],
    notIncludedIT: [],
    notIncludedEN: [],
    featured: true,
    color: '#00D4FF',
    descIT: 'La scelta più popolare. Abbastanza tempo per esplorare il lago, migliorare la tecnica e godersi il tramonto sull\'acqua. Ideale per coppie e piccoli gruppi.',
    descEN: 'The most popular choice. Enough time to explore the lake, improve technique, and enjoy the sunset on the water. Ideal for couples and small groups.',
  },
  {
    id: 'pkg_10h',
    nameIT: '10 Ore',
    nameEN: '10 Hours',
    taglineIT: 'Per chi vuole davvero padroneggiare l\'e-foil',
    taglineEN: 'For those who want to truly master it',
    durationIT: '10 ore',
    durationEN: '10 hours',
    price: 800,
    depositAllowed: true,
    featuresIT: [
      'Tutto incluso nel pacchetto 4h',
      'Sessioni distribuite su più giorni',
      'Deposito 30% disponibile',
      'Priorità nella scelta degli slot',
      'Video Insta360 disponibile (+€45)',
      'Accesso entrambe le location',
    ],
    featuresEN: [
      'Everything in 4h package',
      'Sessions spread over multiple days',
      '30% deposit available',
      'Priority slot selection',
      'Insta360 video available (+€45)',
      'Access to both locations',
    ],
    notIncludedIT: [],
    notIncludedEN: [],
    color: '#E8A94D',
    descIT: 'Per chi vuole davvero padroneggiare l\'e-foil. Le sessioni possono essere distribuite su più weekend, permettendoti di progredire costantemente e raggiungere un livello avanzato.',
    descEN: 'For those who want to truly master the e-foil. Sessions can be spread over multiple weekends, allowing you to progress steadily and reach an advanced level.',
  },
];

export default function PricingCards({ lang = 'it' }: PricingCardsProps) {
  const [expandedPkg, setExpandedPkg] = useState<string | null>('pkg_4h');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {packages.map((pkg) => {
        const isExpanded = expandedPkg === pkg.id;
        return (
          <div
            key={pkg.id}
            className={`card-package${pkg.featured ? ' featured' : ''}`}
            style={{ position: 'relative', overflow: 'hidden' }}
          >
            {pkg.featured && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                background: 'linear-gradient(90deg, #00D4FF, #33ddff)',
              }} />
            )}

            {/* Header row */}
            <div
              style={{ padding: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px' }}
              onClick={() => setExpandedPkg(isExpanded ? null : pkg.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setExpandedPkg(isExpanded ? null : pkg.id)}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                  <h3 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '22px', fontWeight: 700 }}>
                    {lang === 'it' ? pkg.nameIT : pkg.nameEN}
                  </h3>
                  {pkg.featured && (
                    <div className="badge badge-cyan" style={{ fontSize: '10px' }}>
                      {lang === 'it' ? '🔥 Più scelto' : '🔥 Most popular'}
                    </div>
                  )}
                  {pkg.id === 'pkg_10h' && (
                    <div className="badge badge-gold" style={{ fontSize: '10px' }}>
                      {lang === 'it' ? '💎 Miglior valore' : '💎 Best value'}
                    </div>
                  )}
                </div>
                <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.5)' }}>
                  {lang === 'it' ? pkg.taglineIT : pkg.taglineEN}
                </p>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', justifyContent: 'flex-end' }}>
                  <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '32px', fontWeight: 700, color: pkg.color }}>
                    €{pkg.price}
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: 'rgba(240,247,255,0.4)' }}>
                  / {lang === 'it' ? pkg.durationIT : pkg.durationEN}
                </p>
              </div>

              <Icon
                name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                size={20}
                style={{ color: 'rgba(240,247,255,0.4)', flexShrink: 0 }}
              />
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div style={{ padding: '0 24px 24px', borderTop: '1px solid rgba(240,247,255,0.06)' }}>
                <p style={{ fontSize: '14px', color: 'rgba(240,247,255,0.65)', lineHeight: 1.7, margin: '20px 0' }}>
                  {lang === 'it' ? pkg.descIT : pkg.descEN}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  {/* Included */}
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#25D366', marginBottom: '12px' }}>
                      {lang === 'it' ? '✓ Incluso' : '✓ Included'}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(lang === 'it' ? pkg.featuresIT : pkg.featuresEN).map((f) => (
                        <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px' }}>
                          <Icon name="CheckCircleIcon" size={14} variant="solid" style={{ color: '#25D366', flexShrink: 0, marginTop: '2px' }} />
                          <span style={{ color: 'rgba(240,247,255,0.75)' }}>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Not included */}
                  {(lang === 'it' ? pkg.notIncludedIT : pkg.notIncludedEN).length > 0 && (
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.35)', marginBottom: '12px' }}>
                        {lang === 'it' ? 'Note' : 'Notes'}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(lang === 'it' ? pkg.notIncludedIT : pkg.notIncludedEN).map((f) => (
                          <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px' }}>
                            <Icon name="InformationCircleIcon" size={14} style={{ color: 'rgba(240,247,255,0.3)', flexShrink: 0, marginTop: '2px' }} />
                            <span style={{ color: 'rgba(240,247,255,0.45)' }}>{f}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Deposit info */}
                {pkg.depositAllowed && (
                  <div className="alert-info" style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                    <Icon name="CreditCardIcon" size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span style={{ fontSize: '13px' }}>
                      {lang === 'it'
                        ? `Deposito 30% disponibile: paga €${Math.round(pkg.price * 0.3)} ora, il resto in loco.`
                        : `30% deposit available: pay €${Math.round(pkg.price * 0.3)} now, the rest on site.`}
                    </span>
                  </div>
                )}

                <Link
                  href={`/booking?pkg=${pkg.id}`}
                  className={pkg.featured ? 'btn-cyan' : 'btn-primary'}
                  style={{ display: 'inline-flex', fontSize: '14px', padding: '14px 28px' }}
                >
                  <Icon name="CalendarDaysIcon" size={16} variant="solid" />
                  {lang === 'it' ? 'Prenota questo pacchetto' : 'Book this package'}
                </Link>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}